import { useState, useEffect } from 'react';
import { useLocalMedia, useLocalMediaActions } from '../useLocalMedia';

enum MediaActionName {
  ToggleCamera = 'togglecamera',
  ToggleMicrophone = 'togglemicrophone',
}

// TODO: move to a more appropriate place
declare global {
  interface MediaSession {
    setActionHandler(
      action:
        | MediaSessionAction
        | MediaActionName.ToggleCamera
        | MediaActionName.ToggleMicrophone,
      listener: (() => void) | null,
    ): void;
  }
}

export type UsePictureInPictureActionsReturnType = {
  registerActionHandler: () => void;
  deregisterActionHandler: () => void;
};

const isPictureInPictureActionsSupported =
  typeof window !== 'undefined' &&
  Boolean(
    // @ts-expect-error setCameraActive/setMicrophoneActive are non-standard APIs used for PiP feature detection
    window.navigator?.mediaSession?.setCameraActive &&
      window.navigator?.mediaSession?.setMicrophoneActive,
  );

export const usePictureInPictureActions =
  (): UsePictureInPictureActionsReturnType => {
    const [isRegistered, setIsRegistered] = useState(false);
    const { hasVideo, hasAudio } = useLocalMedia();
    const { toggleVideo, toggleAudio } = useLocalMediaActions();

    useEffect(() => {
      if (typeof window === 'undefined' || !isRegistered) {
        return;
      }
      window.navigator.mediaSession.setCameraActive?.(hasVideo);
    }, [isRegistered, hasVideo]);

    useEffect(() => {
      if (typeof window === 'undefined' || !isRegistered) {
        return;
      }
      window.navigator.mediaSession.setMicrophoneActive?.(hasAudio);
    }, [isRegistered, hasAudio]);

    const registerActionHandler = (): void => {
      if (
        typeof window === 'undefined' ||
        !isPictureInPictureActionsSupported
      ) {
        return;
      }

      window.navigator.mediaSession.setActionHandler(
        MediaActionName.ToggleCamera,
        () => void toggleVideo(),
      );
      window.navigator.mediaSession.setActionHandler(
        MediaActionName.ToggleMicrophone,
        () => void toggleAudio(),
      );

      setIsRegistered(true);
    };

    const deregisterActionHandler = (): void => {
      if (
        typeof window === 'undefined' ||
        !isPictureInPictureActionsSupported
      ) {
        return;
      }

      window.navigator.mediaSession.setActionHandler(
        MediaActionName.ToggleCamera,
        null,
      );
      window.navigator.mediaSession.setActionHandler(
        MediaActionName.ToggleMicrophone,
        null,
      );

      setIsRegistered(false);
    };

    return {
      registerActionHandler,
      deregisterActionHandler,
    };
  };
